import React from "react";
import { Typography, IconButton } from "@mui/material";
import NoDataFound from "../../../common/NoDataFound";
import "./assessmentlist.scss";

/**
 * Assessment List Component
 *
 * Shows a list of completed risk assessments in card format
 */
const AssessmentList = ({ assessments, onClose, onViewReport, onRetake }) => {
  // Filter to only show successful/completed assessments
  const completedAssessments = React.useMemo(() => {
    if (!assessments || assessments.length === 0) return [];

    return assessments.filter((assessment) => {
      const status = assessment.status?.toLowerCase() || "success";
      return (
        status === "success" || status === "completed" || !assessment.status
      );
    });
  }, [assessments]);

  // Get assessment title
  const getAssessmentTitle = (assessment) => {
    return (
      assessment.title ||
      assessment.name ||
      assessment.type ||
      "Risk Assessment"
    );
  };

  // Get date string from assessment
  const getDateString = (assessment) => {
    return (
      assessment.assessedAt ||
      assessment.createdAt ||
      assessment.date ||
      assessment.created_at ||
      assessment.assessed_at ||
      assessment.timestamp
    );
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "Date not available";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "Date not available";
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Date not available";
    }
  };

  // Format time for display
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "";
    }
  };

  // Icons
  const CloseIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
        fill="currentColor"
      />
    </svg>
  );

  const PlayIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 16.5V7.5L16 12L10 16.5Z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className="assessment-list-container">
      {/* Header */}
      <div className="list-header">
        <IconButton className="close-btn" onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Typography className="header-title">Risk Assessments</Typography>
        <div className="header-spacer" />
      </div>

      {/* Assessment Cards */}
      <div className="assessments-content">
        {completedAssessments.length === 0 ? (
          <NoDataFound
            icon="assessment"
            message="No assessments done yet"
            description="Complete a mobility assessment to generate insights and track your progress over time."
          />
        ) : (
          <div className="assessment-cards">
            {completedAssessments.map((assessment, index) => (
              <div key={assessment.id || index} className="assessment-card">
                <Typography className="card-title">
                  {getAssessmentTitle(assessment)}
                </Typography>

                <div className="card-row">
                  <div className="card-datetime">
                    <Typography
                      className="card-date"
                      style={{ color: "#666", fontSize: "14px" }}
                    >
                      {formatDate(getDateString(assessment))}
                    </Typography>
                    {formatTime(getDateString(assessment)) && (
                      <Typography
                        className="card-time"
                        style={{ color: "#999", fontSize: "14px" }}
                      >
                        {formatTime(getDateString(assessment))}
                      </Typography>
                    )}
                  </div>
                  <button
                    className="btn btn-primary view-report-btn"
                    onClick={() => onViewReport(assessment)}
                  >
                    View Report
                  </button>
                </div>

                {/* Test Videos Section (if available) */}
                {(assessment.videos?.length > 0 || assessment.hasVideo) && (
                  <div className="test-videos-section">
                    <Typography className="videos-label">
                      Test Video(s)
                    </Typography>
                    <div className="video-items">
                      {assessment.videos ? (
                        assessment.videos.map((video, vIdx) => (
                          <button
                            key={vIdx}
                            className="video-item"
                            onClick={() =>
                              video.url && window.open(video.url, "_blank")
                            }
                          >
                            <PlayIcon />
                            <span>
                              {video.name || getAssessmentTitle(assessment)}
                            </span>
                          </button>
                        ))
                      ) : (
                        <button className="video-item">
                          <PlayIcon />
                          <span>{getAssessmentTitle(assessment)}</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Take New Test Button */}
      <div className="list-footer">
        <button className="btn btn-outline take-test-btn" onClick={onRetake}>
          Take New Assessment
        </button>
      </div>
    </div>
  );
};

export default AssessmentList;
